import { escapeRegExp } from '@Core/utils/strings';
import { TraceMap, originalPositionFor, type SourceMapInput } from '@jridgewell/trace-mapping';

type PageSourceDiagnostic = {
  sourcePath: string;
  codeHash: string;
  sourceMap: SourceMapInput | null;
  lineOffset: number;
};

type RegisteredPageSourceDiagnostic = PageSourceDiagnostic & {
  traceMap: TraceMap | null;
  locationPattern: RegExp;
};

export type OriginalPageSourceLocation = {
  sourcePath: string;
  codeHash: string;
  line: number;
  column: number | null;
};

const sourceDiagnostics = new Map<string, RegisteredPageSourceDiagnostic>();

export function hashPageSource(code: string): string {
  let hash = 0x811c9dc5;

  for (let index = 0; index < code.length; index += 1) {
    hash ^= code.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }

  return (hash >>> 0).toString(16).padStart(8, '0');
}

// The authored file paths the runtime uses internally, mapped to the short
// labels an author sees. `sourcePath` stays the telemetry identifier; the label
// is what appears in stack frames, `//# sourceURL` comments, and preview
// diagnostics, so it has to read like the thing the author edits.
const SOURCE_LABEL_PATTERNS: Array<{ pattern: RegExp; label: string }> = [
  { pattern: /^src\/pages\/(.+)\.tsx$/, label: 'page' },
  { pattern: /^src\/layouts\/(.+)\.tsx$/, label: 'layout' },
  { pattern: /^src\/components\/(.+)\.tsx$/, label: 'component' }
];

export function pageSourceUrl(sourcePath: string): string {
  const normalizedPath = sourcePath.replace(/^\/+/, '');

  for (const { pattern, label } of SOURCE_LABEL_PATTERNS) {
    const match = pattern.exec(normalizedPath);

    if (match) {
      return `diagonal://${label}/${encodeURIComponent(match[1])}`;
    }
  }

  return `diagonal://source/${normalizedPath
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/')}`;
}

export function registerPageSourceDiagnostics(
  sourceUrl: string,
  diagnostic: PageSourceDiagnostic
): void {
  const registered = sourceDiagnostics.get(sourceUrl);

  sourceDiagnostics.set(sourceUrl, {
    ...diagnostic,
    locationPattern:
      registered?.locationPattern ?? new RegExp(`${escapeRegExp(sourceUrl)}:(\\d+):(\\d+)`),
    traceMap:
      diagnostic.sourceMap === null
        ? null
        : registered?.sourceMap === diagnostic.sourceMap
          ? registered.traceMap
          : new TraceMap(diagnostic.sourceMap)
  });
}

function mappedLocation(
  diagnostic: RegisteredPageSourceDiagnostic,
  stackLine: number,
  stackColumn: number
): OriginalPageSourceLocation | null {
  const generatedLine = stackLine - diagnostic.lineOffset;

  if (generatedLine < 1) {
    return null;
  }

  try {
    if (!diagnostic.traceMap) {
      throw new Error('No source map is available.');
    }

    const original = originalPositionFor(diagnostic.traceMap, {
      line: generatedLine,
      column: Math.max(0, stackColumn - 1)
    });

    if (original.line !== null) {
      return {
        sourcePath: diagnostic.sourcePath,
        codeHash: diagnostic.codeHash,
        line: original.line,
        column: original.column
      };
    }
  } catch {
    // Sucrase preserves source lines, so the offset-only location is still useful.
  }

  return {
    sourcePath: diagnostic.sourcePath,
    codeHash: diagnostic.codeHash,
    line: generatedLine,
    column: null
  };
}

export function resolveOriginalLocation(error: Error): OriginalPageSourceLocation | null {
  try {
    if (!error.stack) {
      return null;
    }

    for (const frame of error.stack.split('\n')) {
      for (const [sourceUrl, diagnostic] of sourceDiagnostics) {
        if (!frame.includes(sourceUrl)) {
          continue;
        }

        const match = diagnostic.locationPattern.exec(frame);

        if (match) {
          return mappedLocation(diagnostic, Number(match[1]), Number(match[2]));
        }
      }
    }
  } catch {
    return null;
  }

  return null;
}
